// ABOUTME: Renders a federated module's Root inside an ErrorBoundary with a host context
// ABOUTME: Loads the module via react-query (cached forever, no retry) keyed by slug

import type { ModuleHostContext } from '@alpha-finder/module-contract';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { currentToken } from '../lib/auth-client';
import { loadModule } from '../lib/module-loader';
import { ErrorBoundary } from './ErrorBoundary';
import { ModuleErrorCard } from './ModuleErrorCard';
import { ModuleSkeleton } from './ModuleSkeleton';

export function ModuleHost({ slug }: { slug: string }) {
  const queryClient = useQueryClient();
  const { claims } = useAuth();

  const {
    data: mod,
    error,
    isPending,
  } = useQuery({
    queryKey: ['module', slug],
    queryFn: () => loadModule(slug),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  if (isPending) return <ModuleSkeleton />;
  if (error || !mod) {
    return <ModuleErrorCard slug={slug} message={(error as Error | undefined)?.message} />;
  }

  const ctx: ModuleHostContext = {
    userId: claims?.uid ?? '',
    baseApiUrl: '',
    modulePath: `/modules/${slug}`,
    apiPath: `/api/modules/${slug}`,
    queryClient,
    theme: 'light', // theme switching is a v0.3 polish item
    getToken: currentToken,
  };

  const Root = mod.Root;
  return (
    <ErrorBoundary>
      <Root ctx={ctx} />
    </ErrorBoundary>
  );
}
